import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import apiClient from '@/api/client'
import { getStoredUserRole } from '@/lib/staff-roles'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  ArrowRightLeft,
  ChefHat,
  History,
  LifeBuoy,
  MessageSquareWarning,
  Printer,
  Split,
  Trash2,
  User,
  Users,
  UtensilsCrossed,
} from 'lucide-react'
import type { CustomerQuickAddress, DiningTable, Order } from '@/types'
import {
  CounterGuestDetailsBody,
  hasGuestDetailsDraft,
} from '@/components/counter/CounterGuestDetailsSection'
import { CounterTableServiceBody } from '@/components/counter/CounterTableServiceSection'
import { CounterOrderHistoryBody } from '@/components/counter/CounterOrderHistorySection'
import { RecoveryCouponModal } from '@/components/counter/RecoveryCouponModal'
import { withUndo } from '@/lib/toast-helpers'

export interface CounterRailToolbarProps {
  orderType: string
  existingOrder: Order | null
  continuingOrderId: string | null
  selectedTable: DiningTable | null

  // Guest CRM
  customerName: string
  setCustomerName: (v: string) => void
  customerEmail: string
  setCustomerEmail: (v: string) => void
  customerPhone: string
  setCustomerPhone: (v: string) => void
  guestBirthday: string
  setGuestBirthday: (v: string) => void
  deliveryAddress: string
  setDeliveryAddress: (v: string) => void
  /** Saved addresses of the picked customer — tap-to-switch chips on delivery. */
  savedAddresses?: CustomerQuickAddress[]
  setSavedAddresses?: (v: CustomerQuickAddress[]) => void
  onGuestUpdated: (order: Order) => void

  // Party + server
  onServiceUpdated: (order: Order) => void

  // Order history
  onSelectHistoryOrder: (order: Order) => void
  formatCurrency: (n: number) => string

  // Table actions
  onPickTable: () => void
  onChangeTable: () => void
  onCancelSession: (orderId: string) => void
  cancelSessionPending: boolean

  // Split bill (F-FE-N19) — omit onOpenSplit to hide the button.
  onOpenSplit?: () => void
  /** True when a split session exists on the order being paid. */
  splitActive?: boolean
  /** Disable reason shown in the tooltip; undefined = enabled. */
  splitDisabledReason?: string

  /**
   * Reprint the fired kitchen ticket(s) — omit to hide the button (no fired
   * lines on the loaded order). 'here' prints on this counter's receipt
   * printer; 'kitchen' resends to the station printers like a fire.
   */
  onReprintKot?: (mode: 'here' | 'kitchen') => void
  reprintKotPending?: boolean
}

/**
 * Compact icon-toolbar that lives directly under the `TicketHeader` and hosts
 * the Guest, Party + server, and Order history popovers along with contextual
 * actions (pick / change table, cancel session, close checkout).
 *
 * The previous rail mounted these as always-expanded `<details>` blocks which
 * collectively consumed ~280–500 px of vertical space at the top of the
 * sidebar; collapsing them into icon-triggered popovers gives that space back
 * to the cart and the primary CTA.
 */
export function CounterRailToolbar({
  orderType,
  existingOrder,
  continuingOrderId,
  selectedTable,
  customerName,
  setCustomerName,
  customerEmail,
  setCustomerEmail,
  customerPhone,
  setCustomerPhone,
  guestBirthday,
  setGuestBirthday,
  deliveryAddress,
  setDeliveryAddress,
  savedAddresses,
  setSavedAddresses,
  onGuestUpdated,
  onServiceUpdated,
  onSelectHistoryOrder,
  formatCurrency,
  onPickTable,
  onChangeTable,
  onCancelSession,
  cancelSessionPending,
  onOpenSplit,
  splitActive,
  splitDisabledReason,
  onReprintKot,
  reprintKotPending,
}: CounterRailToolbarProps) {
  const [guestOpen, setGuestOpen] = useState(false)
  const [reprintOpen, setReprintOpen] = useState(false)
  // True while the loyalty-card viewfinder is live inside the guest popover.
  // Chrome's camera permission bubble takes focus away from the document, which
  // Radix reads as a focus-outside and would use to close the popover — killing
  // the stream during the very prompt that authorises it.
  const [guestCameraActive, setGuestCameraActive] = useState(false)
  const [partyOpen, setPartyOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [cancelSessionDialogOpen, setCancelSessionDialogOpen] = useState(false)
  const [recoveryOpen, setRecoveryOpen] = useState(false)
  const router = useRouter()

  // Unhappy-guest indicator. Only mounted for the roles that can actually act
  // on it (the recovery worklist is admin+manager), because a badge a cashier
  // cannot open is just anxiety. Deliberately NOT a toast or a modal: checkout
  // already contends with the closing banner, offline tray, PRA prompt and
  // ready-order notifications, and a marketing interrupt on a money path earns
  // nothing while risking a mis-tender.
  const canSeeRecovery = ['admin', 'manager'].includes(getStoredUserRole())
  const recoveryPulse = useQuery({
    queryKey: ['marketing', 'recovery-pulse'],
    queryFn: () => apiClient.getRecoveryPulse(),
    enabled: canSeeRecovery,
    staleTime: 60_000,
    refetchInterval: 120_000,
  })
  const guestsWaitingNow = recoveryPulse.data?.data?.recent_2h ?? 0

  const isDineIn = orderType === 'dine_in'
  const isDelivery = orderType === 'delivery'
  // Delivery orders can't be placed without an address; flag the guest button
  // so the cashier knows where the missing piece lives.
  const deliveryAddressMissing = isDelivery && !deliveryAddress.trim()
  const dineInActive = isDineIn && Boolean(continuingOrderId && existingOrder)

  // Recovery coupon — available on any active (saved) order. The action is
  // PIN-gated server-side; here we just surface it and route to guest capture
  // when no customer is attached yet (the coupon must bind to a customer).
  const activeOrderId = existingOrder?.id ?? continuingOrderId ?? null
  const recoveryCustomerCaptured = Boolean(existingOrder?.customer_id)

  const hasGuestDraft = hasGuestDetailsDraft({
    customerName,
    customerEmail,
    customerPhone,
    guestBirthday,
  })
  const guestCount = existingOrder?.guest_count ?? 0
  const assignedServerFirstName =
    existingOrder?.user?.first_name ??
    (existingOrder?.user?.username ? existingOrder.user.username : null)
  const hasPartyDraft = Boolean(guestCount > 0 || existingOrder?.user_id)

  /**
   * F-CTRL-N70 — the escape hatch is gated on the REAL condition, not on
   * `is_open_tab`.
   *
   * The server-side guard (releaseOpenTabTx) has always been "nothing fired,
   * nothing paid, not already terminal" — it never looked at `is_open_tab`. The
   * button did, so it hid from every dine-in order created through
   * createOrderCore, which hardcodes `is_open_tab = false`. Those are precisely
   * the orders that need it: a create whose KOT fire never landed leaves unsent
   * lines on a table with no way to cancel and (before F-FE-N154) no way to even
   * see them. Parched order 20260910-040 sat like that on a live table and had
   * to be cleared out of band — see
   * audit/PARCHED_UNFIRED_ORDER_NO_VOID_BANNER_2026-09-10.md.
   *
   * `existingOrder` must be loaded rather than inferred from
   * `continuingOrderId`: this decides on the order's real state, and a bare id
   * tells us nothing about whether it was fired or paid.
   */
  const cancelSessionOrderId = existingOrder?.id ?? continuingOrderId ?? null
  const orderHasCompletedPayment = (existingOrder?.payments ?? []).some(
    (p) => p.status === 'completed',
  )
  //
  // `isDineIn` rather than `dineInActive`: the latter also requires
  // `continuingOrderId`, which is armed only while a tab is genuinely
  // collecting more items (shouldKeepCollecting). That was the second of the
  // three gates hiding this button. `canChangeTable` keeps `dineInActive`
  // untouched — the ticket header carries its own change-table affordance
  // gated on `existingOrder?.id`, which is how 20260910-040 was moved twice
  // while remaining uncancellable.
  const canCancelSession =
    isDineIn &&
    Boolean(existingOrder?.id) &&
    !existingOrder?.kot_first_sent_at &&
    !orderHasCompletedPayment

  const canChangeTable = dineInActive

  /** If the trash button hides (rules change), tear down any open modal. */
  useEffect(() => {
    if (!canCancelSession) setCancelSessionDialogOpen(false)
  }, [canCancelSession])

  const confirmCancelSession = () => {
    // F-CTRL-N70: was `continuingOrderId`, which is only armed when a tab is
    // genuinely collecting more items (see shouldKeepCollecting). A loaded
    // order that is not appending has none, so the button rendered and the
    // confirm silently did nothing — a second hidden gate behind the first.
    if (!cancelSessionOrderId) return
    const orderId = cancelSessionOrderId
    setCancelSessionDialogOpen(false)
    withUndo({
      title: 'Session will be cancelled',
      description: 'Tap Undo to keep the session.',
      durationMs: 6000,
      onCommit: () => onCancelSession(orderId),
    })
  }

  const tableLabel = selectedTable
    ? `T${String(selectedTable.table_number)}`
    : existingOrder?.table?.table_number
      ? `T${String(existingOrder.table.table_number)}`
      : null

  const closeAllPopovers = () => {
    setGuestOpen(false)
    setPartyOpen(false)
    setHistoryOpen(false)
  }

  const onSelectHistoryAndClose = (order: Order) => {
    closeAllPopovers()
    onSelectHistoryOrder(order)
  }

  return (
    <TooltipProvider delayDuration={250}>
      <div
        role="toolbar"
        aria-label="Ticket actions"
        className="flex items-center gap-1 overflow-x-auto border-b border-border/80 bg-card/90 px-3 py-2 sm:px-4"
      >
        {/* Pick / change table — dine-in only */}
        {isDineIn && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant={tableLabel ? 'secondary' : 'default'}
                size="sm"
                className={cn(
                  'h-9 shrink-0 gap-1.5 px-2.5 text-xs font-semibold',
                  !tableLabel && 'shadow-sm'
                )}
                onClick={onPickTable}
                aria-label={tableLabel ? `Change table (current: ${tableLabel})` : 'Pick a table'}
              >
                <UtensilsCrossed className="h-4 w-4" aria-hidden />
                {tableLabel ? (
                  <span className="tabular-nums">{tableLabel}</span>
                ) : (
                  <span>Pick table</span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {tableLabel ? 'Change table' : 'Pick a table to start a session'}
            </TooltipContent>
          </Tooltip>
        )}

        {/* Guest CRM popover */}
        <Popover open={guestOpen} onOpenChange={setGuestOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant={guestOpen ? 'secondary' : 'ghost'}
                  size="icon"
                  className="relative h-9 w-9 shrink-0"
                  aria-label="Guest details"
                  aria-expanded={guestOpen}
                >
                  <User className="h-4 w-4" aria-hidden />
                  {deliveryAddressMissing ? (
                    <span
                      className="absolute right-1 top-1 h-2 w-2 rounded-full bg-destructive ring-2 ring-card"
                      aria-label="Delivery address required"
                    />
                  ) : hasGuestDraft ? (
                    <span
                      className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-primary"
                      aria-hidden
                    />
                  ) : null}
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">Guest details</TooltipContent>
          </Tooltip>
          <PopoverContent
            align="start"
            sideOffset={6}
            className="w-[min(360px,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto p-3"
            // Only focus-outside is blocked, never pointer-outside: a cashier
            // must still be able to click away to dismiss the popover while the
            // viewfinder is running.
            onFocusOutside={(e) => {
              if (guestCameraActive) e.preventDefault()
            }}
          >
            <h3 className="mb-1.5 text-sm font-semibold tracking-tight text-foreground">
              Guest details
            </h3>
            <CounterGuestDetailsBody
              customerName={customerName}
              setCustomerName={setCustomerName}
              customerEmail={customerEmail}
              setCustomerEmail={setCustomerEmail}
              customerPhone={customerPhone}
              setCustomerPhone={setCustomerPhone}
              guestBirthday={guestBirthday}
              setGuestBirthday={setGuestBirthday}
              deliveryAddress={deliveryAddress}
              setDeliveryAddress={setDeliveryAddress}
              savedAddresses={savedAddresses}
              setSavedAddresses={setSavedAddresses}
              showDeliveryAddress={isDelivery}
              existingOrder={existingOrder}
              onCameraActiveChange={setGuestCameraActive}
              onGuestUpdated={onGuestUpdated}
              onClose={() => setGuestOpen(false)}
            />
          </PopoverContent>
        </Popover>

        {/* Party & server — dine-in continuation only */}
        {isDineIn && existingOrder?.id && (
          <Popover open={partyOpen} onOpenChange={setPartyOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant={partyOpen ? 'secondary' : 'ghost'}
                    size="sm"
                    className={cn(
                      'relative h-9 gap-1.5 px-2',
                      !hasPartyDraft && 'w-9 px-0'
                    )}
                    aria-label="Party and server"
                    aria-expanded={partyOpen}
                  >
                    <span className="relative shrink-0">
                      <Users className="h-4 w-4" aria-hidden />
                      {guestCount > 0 && (
                        <span
                          className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-0.5 text-[10px] font-bold leading-none text-primary-foreground"
                          aria-label={`${guestCount} guests`}
                        >
                          {guestCount}
                        </span>
                      )}
                    </span>
                    {assignedServerFirstName && (
                      <span className="max-w-[5rem] truncate text-xs font-medium">
                        {assignedServerFirstName}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">Party &amp; server</TooltipContent>
            </Tooltip>
            <PopoverContent
              align="start"
              sideOffset={6}
              className="w-[min(360px,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto p-3"
            >
              <h3 className="mb-1.5 text-sm font-semibold tracking-tight text-foreground">
                Party &amp; server
              </h3>
              <CounterTableServiceBody
                existingOrder={existingOrder}
                onServiceUpdated={onServiceUpdated}
              />
            </PopoverContent>
          </Popover>
        )}

        {/* Order history popover */}
        <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant={historyOpen ? 'secondary' : 'ghost'}
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label="Order history for current order type"
                  aria-expanded={historyOpen}
                >
                  <History className="h-4 w-4" aria-hidden />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">Order history (current order type)</TooltipContent>
          </Tooltip>
          <PopoverContent
            align="start"
            sideOffset={6}
            className="w-[min(360px,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto p-3"
          >
            <h3 className="mb-1.5 text-sm font-semibold tracking-tight text-foreground">
              Order history
            </h3>
            <CounterOrderHistoryBody
              orderType={orderType}
              formatCurrency={formatCurrency}
              onSelectOrder={onSelectHistoryAndClose}
            />
          </PopoverContent>
        </Popover>

        {/* Split bill — divide the check across guests (F-FE-N19) */}
        {onOpenSplit && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="relative h-9 w-9"
                  disabled={Boolean(splitDisabledReason)}
                  onClick={onOpenSplit}
                  aria-label="Split bill"
                >
                  <Split className="h-4 w-4" aria-hidden />
                  {splitActive && (
                    <span
                      className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-amber-500"
                      aria-label="Split in progress"
                    />
                  )}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {splitDisabledReason ?? (splitActive ? 'Split in progress — resume' : 'Split bill')}
            </TooltipContent>
          </Tooltip>
        )}

        {/* Reprint KOT — only when the loaded order has fired lines */}
        {onReprintKot && (
          <Popover open={reprintOpen} onOpenChange={setReprintOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant={reprintOpen ? 'secondary' : 'ghost'}
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    disabled={reprintKotPending}
                    aria-label="Reprint kitchen ticket"
                    aria-expanded={reprintOpen}
                  >
                    <Printer className="h-4 w-4" aria-hidden />
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">Reprint kitchen ticket (KOT)</TooltipContent>
            </Tooltip>
            <PopoverContent align="start" sideOffset={6} className="w-[min(300px,calc(100vw-2rem))] p-3">
              <h3 className="mb-1 text-sm font-semibold tracking-tight text-foreground">
                Reprint kitchen ticket
              </h3>
              <p className="mb-3 text-xs text-muted-foreground">
                Prints every line already sent to the kitchen, marked REPRINT.
              </p>
              <div className="space-y-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-auto w-full justify-start gap-3 px-3 py-2.5 text-left"
                  disabled={reprintKotPending}
                  onClick={() => {
                    setReprintOpen(false)
                    onReprintKot('here')
                  }}
                >
                  <Printer className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">Print here</span>
                    <span className="block text-xs font-normal text-muted-foreground">
                      This counter's receipt printer
                    </span>
                  </span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-auto w-full justify-start gap-3 px-3 py-2.5 text-left"
                  disabled={reprintKotPending}
                  onClick={() => {
                    setReprintOpen(false)
                    onReprintKot('kitchen')
                  }}
                >
                  <ChefHat className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">Resend to kitchen</span>
                    <span className="block text-xs font-normal text-muted-foreground">
                      Station printers, same routing as Fire
                    </span>
                  </span>
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Contextual actions — same row, no spacer, so the toolbar never wraps */}
        <div className="sticky right-0 flex shrink-0 items-center gap-1 bg-card/95 pl-1 backdrop-blur-sm">
          {canSeeRecovery && guestsWaitingNow > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 gap-1.5 px-2 text-rose-600 hover:bg-rose-500/10 hover:text-rose-600 dark:text-rose-400"
                  onClick={() => router.navigate({ to: '/admin/marketing/recovery' })}
                  aria-label="Unhappy guests waiting"
                >
                  <MessageSquareWarning className="h-4 w-4" aria-hidden />
                  <span className="text-xs font-semibold tabular-nums">{guestsWaitingNow}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {guestsWaitingNow} guest{guestsWaitingNow === 1 ? '' : 's'} rated you badly in the
                last 2 hours — they may still be here
              </TooltipContent>
            </Tooltip>
          )}

          {activeOrderId && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-amber-600 hover:bg-amber-500/10 hover:text-amber-600 dark:text-amber-400"
                  onClick={() => setRecoveryOpen(true)}
                  aria-label="Issue recovery coupon"
                >
                  <LifeBuoy className="h-4 w-4" aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Recovery coupon (next visit)</TooltipContent>
            </Tooltip>
          )}

          {canChangeTable && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  onClick={onChangeTable}
                  aria-label="Move table"
                >
                  <ArrowRightLeft className="h-4 w-4" aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Move table</TooltipContent>
            </Tooltip>
          )}

          {canCancelSession && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  disabled={cancelSessionPending}
                  onClick={() => setCancelSessionDialogOpen(true)}
                  aria-label="Cancel session"
                  aria-haspopup="dialog"
                  aria-expanded={cancelSessionDialogOpen}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Cancel session</TooltipContent>
            </Tooltip>
          )}

        </div>

      </div>

      <Dialog open={cancelSessionDialogOpen} onOpenChange={setCancelSessionDialogOpen}>
        <DialogContent className="max-w-md sm:rounded-lg">
          <DialogHeader>
            <DialogTitle>Cancel session?</DialogTitle>
            <DialogDescription>
              The table will be freed and any staged items lost. Staff can start a fresh session after
              you confirm.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCancelSessionDialogOpen(false)}
              disabled={cancelSessionPending}
            >
              Keep session
            </Button>
            {/* F-CTRL-N70: the third gate. `disabled` was `!continuingOrderId`,
                so even when the button rendered, the dialog's own confirm sat
                disabled on an order that was loaded but not appending. */}
            <Button
              type="button"
              variant="destructive"
              disabled={cancelSessionPending || !cancelSessionOrderId}
              onClick={confirmCancelSession}
            >
              {cancelSessionPending ? 'Cancelling…' : 'Cancel session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {recoveryOpen && activeOrderId && (
        <RecoveryCouponModal
          orderId={activeOrderId}
          customerCaptured={recoveryCustomerCaptured}
          existingOrder={existingOrder}
          customerName={customerName}
          setCustomerName={setCustomerName}
          customerEmail={customerEmail}
          setCustomerEmail={setCustomerEmail}
          customerPhone={customerPhone}
          setCustomerPhone={setCustomerPhone}
          guestBirthday={guestBirthday}
          setGuestBirthday={setGuestBirthday}
          onGuestUpdated={onGuestUpdated}
          onClose={() => setRecoveryOpen(false)}
        />
      )}
    </TooltipProvider>
  )
}
